import { Alert, Stack, Typography } from "@octopus-energy/coral/atoms"
import { FormikCheckbox } from "@octopus-energy/coral/formik"
import { ZodFormik } from "@oefr/ui"
import { useTranslations } from "next-intl"
import z from "zod"
import { FormActions } from "@/components/organisms"
import { type FragmentOf, graphql, readFragment } from "@/graphql"
import { useToast } from "@/hooks/useToast"
import { useUpdateIndustryConsentMutation } from "../../hooks/useUpdateIndustryConsentMutation"
import { type CommonAgreementConsentsProps } from "../../types/consentProps"

const graphQLFragments = {
    agreement: graphql(`
        fragment GasAgreementConsents_Agreement on AgreementType {
            consents {
                collectDailyConsumptionData
            }
            supplyPoint {
                id
            }
        }
    `),
}

const GasAgreementConsentsSchema = z.object({
    collectDailyConsumptionData: z.boolean(),
})

type GasAgreementConsentsProps = CommonAgreementConsentsProps & {
    agreement: FragmentOf<typeof graphQLFragments.agreement>
}

export function GasAgreementConsents(props: GasAgreementConsentsProps) {
    const t = useTranslations("GasAgreementConsents")
    const updateIndustryConsent = useUpdateIndustryConsentMutation()
    const toast = useToast()

    const agreement = readFragment(graphQLFragments.agreement, props.agreement)

    const collectDailyConsumptionData = Boolean(agreement?.consents?.collectDailyConsumptionData)

    const onSubmit = async (consentData: z.infer<typeof GasAgreementConsentsSchema>) => {
        if (!agreement?.supplyPoint?.id) {
            return
        }

        await updateIndustryConsent.mutateAsync(
            {
                supplyPointId: agreement.supplyPoint.id,
                consentData,
            },
            {
                onSuccess: () => {
                    toast("success.update", { type: "success" })
                    props.onCompleted?.()
                },
            }
        )
    }

    return (
        <Stack
            flexDirection="column"
            gap="md"
        >
            {!collectDailyConsumptionData && (
                <Alert severity="warning">
                    <Typography color="inherit">{t("warning")}</Typography>
                </Alert>
            )}
            <ZodFormik
                initialValues={{
                    collectDailyConsumptionData,
                }}
                validationSchema={GasAgreementConsentsSchema}
                onSubmit={onSubmit}
                enableReinitialize
            >
                <Stack
                    flexDirection="column"
                    gap="md"
                >
                    <Typography textStyle="body2">{t("authorization")}</Typography>
                    <Stack
                        gap="xxs"
                        justifyContent="space-between"
                    >
                        <FormikCheckbox
                            name={"collectDailyConsumptionData"}
                            disabled={props.disabled}
                            label={t("dailyConsumptionData")}
                        />
                    </Stack>
                    {!props.disabled && (
                        <FormActions>
                            <FormActions.Error error={updateIndustryConsent.error} />
                            <FormActions.Accept
                                size="small"
                                color="primary500"
                            />
                        </FormActions>
                    )}
                </Stack>
            </ZodFormik>
        </Stack>
    )
}

GasAgreementConsents.messages = [
    "GasAgreementConsents",
    ...useToast.messages,
    ...FormActions.Error.messages,
] as const

GasAgreementConsents.fragments = graphQLFragments
